import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputAdornment, Paper, Stack, TextField, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { EstablecimientoSelect } from '@/features/establecimientos/components/EstablecimientoSelect';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { ProfesionalSelect } from '@/features/profesionales/components/ProfesionalSelect';

import type { ProtocolosFilters } from '../hooks/useProtocolosFilters';

interface ProtocolosFiltersBarProps {
  filters: ProtocolosFilters;
  onChange: (partial: Partial<ProtocolosFilters>) => void;
  onClear: () => void;
}

const DEBOUNCE_MS = 500;

export function ProtocolosFiltersBar({
  filters,
  onChange,
  onClear,
}: ProtocolosFiltersBarProps): JSX.Element {
  const currentUser = useAuthStore((s) => s.currentUser);
  const canSeeFilters =
    currentUser?.rol === 'administrador' || currentUser?.rol === 'tecnico_laboratorio';

  const [inputValue, setInputValue] = useState(filters.numeroProtocolo);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync cuando se limpia desde afuera (botón limpiar)
  useEffect(() => {
    setInputValue(filters.numeroProtocolo);
  }, [filters.numeroProtocolo]);

  function handleNumeroChange(val: string) {
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ numeroProtocolo: val, uidProfesional: '', uidEstablecimiento: '' });
    }, DEBOUNCE_MS);
  }

  function handleNumeroKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      onChange({ numeroProtocolo: inputValue, uidProfesional: '', uidEstablecimiento: '' });
    }
  }

  function handleProfesionalChange(uid: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setInputValue('');
    onChange({ uidProfesional: uid, uidEstablecimiento: '', numeroProtocolo: '' });
  }

  function handleEstablecimientoChange(uid: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setInputValue('');
    onChange({ uidEstablecimiento: uid, uidProfesional: '', numeroProtocolo: '' });
  }

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.5 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'stretch', md: 'center' }}
        flexWrap="wrap"
      >
        <TextField
          size="small"
          label="N° Protocolo"
          placeholder="Número exacto"
          value={inputValue}
          onChange={(e) => handleNumeroChange(e.target.value.replace(/\D/g, ''))}
          onKeyDown={handleNumeroKeyDown}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 160 }}
        />

        {canSeeFilters && (
          <>
            <Box sx={{ minWidth: 200 }}>
              <ProfesionalSelect
                value={filters.uidProfesional}
                onChange={(e) => handleProfesionalChange(e.target.value as string)}
                size="small"
                includeEmpty
                emptyLabel="Todos"
                label="Profesional"
              />
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <EstablecimientoSelect
                value={filters.uidEstablecimiento}
                onChange={handleEstablecimientoChange}
                size="small"
                includeEmpty
                emptyLabel="Todos"
                label="Establecimiento"
              />
            </Box>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Limpiar filtros">
          <IconButton size="small" onClick={onClear}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
